import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { flowsApi } from "./client";

export const flowsKeys = {
  all: ["flows"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["flows", "list", q] as const,
  detail: (id: string) => ["flows", "detail", id] as const,
  steps: (flowId: string) => ["flows", "steps", flowId] as const,
  connections: (flowId: string) => ["flows", "connections", flowId] as const,
  mappings: (flowId: string) => ["flows", "mappings", flowId] as const,
};

export function useFlowsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: flowsKeys.list(q),
    queryFn: () => flowsApi.list(q),
  });
}

export function useFlowQuery(
  id: string,
  includeSteps = false,
  includeConnections = false,
) {
  return useQuery({
    queryKey: flowsKeys.detail(id),
    queryFn: () => flowsApi.get(id, includeSteps, includeConnections),
    enabled: !!id,
  });
}

export function useCreateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      description?: string;
      purpose?: string;
      departmentId?: string;
      status?: "active" | "draft" | "inactive";
    }) => flowsApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງ Flow ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: flowsKeys.all });
    },
    onError: () => toast.error("ສ້າງ Flow ລົ້ມເຫຼວ"),
  });
}

export function useDuplicateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => flowsApi.duplicate(id),
    onSuccess: () => {
      toast.success("ຄັດລອກ Flow ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: flowsKeys.all });
    },
    onError: () => toast.error("ຄັດລອກ Flow ລົ້ມເຫຼວ"),
  });
}

export function useUpdateFlow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title?: string;
      description?: string;
      purpose?: string;
      departmentId?: string;
      status?: "active" | "draft" | "inactive";
    }) => flowsApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂ Flow ສໍາເລັດ");
      qc.invalidateQueries({ queryKey: flowsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: flowsKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂ Flow ລົ້ມເຫຼວ"),
  });
}

export function useDeleteFlow() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => flowsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useFlowSteps(flowId: string) {
  return useQuery({
    queryKey: flowsKeys.steps(flowId),
    queryFn: () => flowsApi.getSteps(flowId),
    enabled: !!flowId,
  });
}

export function useCreateFlowStep(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      stepNumber: number;
      title: string;
      description?: string;
      responsiblePositionId?: string;
      departmentId?: string;
      stepTypeId?: string;
      notes?: string;
      positionX?: number;
      positionY?: number;
    }) => flowsApi.createStep(flowId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.steps(flowId) });
      qc.invalidateQueries({ queryKey: flowsKeys.detail(flowId) });
    },
  });
}

export function useUpdateFlowStep(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stepId,
      data,
    }: {
      stepId: string;
      data: {
        stepNumber?: number;
        title?: string;
        description?: string;
        responsiblePositionId?: string;
        departmentId?: string;
        stepTypeId?: string;
        notes?: string;
        positionX?: number;
        positionY?: number;
      };
    }) => flowsApi.updateStep(stepId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.steps(flowId) });
      qc.invalidateQueries({ queryKey: flowsKeys.detail(flowId) });
    },
  });
}

export function useDeleteFlowStep(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stepId: string) => flowsApi.deleteStep(stepId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.steps(flowId) });
      qc.invalidateQueries({ queryKey: flowsKeys.detail(flowId) });
    },
  });
}

export function useFlowConnections(flowId: string) {
  return useQuery({
    queryKey: flowsKeys.connections(flowId),
    queryFn: () => flowsApi.getConnections(flowId),
    enabled: !!flowId,
  });
}

export function useCreateFlowConnection(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      fromStepId: string;
      toStepId: string;
      label?: string;
    }) => flowsApi.createConnection(flowId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.connections(flowId) });
      qc.invalidateQueries({ queryKey: flowsKeys.detail(flowId) });
    },
  });
}

export function useDeleteFlowConnections(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => flowsApi.deleteConnections(flowId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.connections(flowId) });
      qc.invalidateQueries({ queryKey: flowsKeys.detail(flowId) });
    },
  });
}

export function useUpdateFlowConnection(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      connectionId,
      data,
    }: {
      connectionId: string;
      data: { fromStepId?: string; toStepId?: string; label?: string | null };
    }) => flowsApi.updateConnection(connectionId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.connections(flowId) });
      qc.invalidateQueries({ queryKey: flowsKeys.detail(flowId) });
    },
  });
}

export function useDeleteFlowConnection(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      flowsApi.deleteConnection(connectionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.connections(flowId) });
      qc.invalidateQueries({ queryKey: flowsKeys.detail(flowId) });
    },
  });
}

export function useFlowMappings(flowId: string) {
  return useQuery({
    queryKey: flowsKeys.mappings(flowId),
    queryFn: () => flowsApi.getMappings(flowId),
    enabled: !!flowId,
  });
}

export function useCreateFlowMapping(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      sopId?: string | null;
      departmentId?: string | null;
      positionId?: string | null;
    }) => {
      if (!flowId || flowId.trim() === "") {
        throw new Error("Flow ID is required");
      }
      return flowsApi.createMapping(flowId, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.mappings(flowId) });
    },
  });
}

export function useUpdateFlowMapping(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      mappingId,
      data,
    }: {
      mappingId: string;
      data: {
        sopId?: string | null;
        departmentId?: string | null;
        positionId?: string | null;
      };
    }) => flowsApi.updateMapping(mappingId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: flowsKeys.mappings(flowId) });
    },
  });
}

export function useDeleteFlowMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mappingId: string) => flowsApi.deleteMapping(mappingId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flows", "mappings"] });
    },
  });
}
